import {
    compactPendoMetadata,
    PendoService,
    type PendoIdentity,
    type PendoServiceOptions
} from '@tadpoles/pendo'
import type { Child, DeepReadonly, Person } from '@tadpoles/common'
import { untrack } from 'svelte'

import type { AppContext } from '../context/app-context.svelte.js'
import type {
    AuthenticationService,
    AuthenticationState
} from './authentication/authentication.svelte.js'

export type FamilyPendoServiceOptions = PendoServiceOptions

const PreAuthenticationIdentity: PendoIdentity = {
    visitor: {
        id: 'family-pre-auth'
    },
    account: {
        id: 'family-app'
    }
}

export class FamilyPendoService {
    readonly #appContext: AppContext
    readonly #auth: AuthenticationService
    readonly #destroyIdentityObserver: () => void
    readonly #pendo: PendoService

    public constructor(
        auth: AuthenticationService,
        appContext: AppContext,
        options: FamilyPendoServiceOptions = {}
    ) {
        this.#appContext = appContext
        this.#auth = auth
        this.#pendo = new PendoService(options)

        // Author note: Pendo identity synchronization is an external analytics side
        // effect, and settled auth/app-context state is sufficient. untrack keeps the
        // effect scoped to those external inputs instead of any state touched by the Pendo
        // sync path. $derived would only compute metadata, event handlers/bindings/
        // attachments are UI or element scoped, and onMount is component scoped rather
        // than service scoped.
        this.#destroyIdentityObserver = $effect.root(() => {
            $effect(() => {
                const authState = this.#auth.state
                const person = this.#appContext.person
                const child = this.#appContext.child

                untrack(() => {
                    this.#sync(authState, person, child)
                })
            })
        })
    }

    public notify = (name: string, metadata: pendo.Metadata = {}): boolean =>
        this.track(name, metadata)

    public track(name: string, metadata: pendo.Metadata = {}): boolean {
        this.#sync(this.#auth.state, this.#appContext.person, this.#appContext.child)
        return this.#pendo.track(name, metadata)
    }

    public trackPreAuthentication(name: string, metadata: pendo.Metadata = {}): boolean {
        return this.#pendo.trackWithIdentity(
            PreAuthenticationIdentity,
            name,
            metadata,
            compactPendoMetadata({
                app: 'family',
                authPhase: this.#auth.state.phase
            })
        )
    }

    public destroy(): void {
        this.#destroyIdentityObserver()
    }

    #sync(
        authState: AuthenticationState,
        person: DeepReadonly<Person> | undefined,
        child: Child | undefined
    ): void {
        if (authState.phase !== 'authenticated' || !person) {
            this.#pendo.clearIdentity()
            return
        }

        this.#pendo.identify(
            this.#createIdentity(person, child),
            this.#createContextMetadata(authState, person, child)
        )
    }

    #createIdentity(person: DeepReadonly<Person>, child: Child | undefined): PendoIdentity {
        const visitor: pendo.IdentityMetadata = {
            id: person.uid ?? person.key,
            ...compactPendoMetadata({
                personKey: person.key,
                personUid: person.uid,
                displayName: person.displayName,
                language: person.language,
                roles: [...person.roles],
                selectedChildKey: child?.key,
                selectedChildStatus: child?.status
            })
        }
        const account: pendo.IdentityMetadata = {
            id: person.key,
            ...compactPendoMetadata({
                app: 'family',
                personKey: person.key
            })
        }

        return {
            visitor,
            account
        }
    }

    #createContextMetadata(
        authState: AuthenticationState,
        person: DeepReadonly<Person> | undefined,
        child: Child | undefined
    ): pendo.Metadata {
        return compactPendoMetadata({
            authPhase: authState.phase,
            personKey: person?.key,
            personUid: person?.uid,
            selectedChildKey: child?.key,
            selectedChildStatus: child?.status
        })
    }
}
